import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '../../auth/useSession.js';
import {
  lireComptabilite,
  listerBeneficiaires,
  listerEncaissements,
  listerTypesRecette,
  listerVersionsGrille,
} from '../../lib/api.js';

/** La racine des clés de la comptabilité : une écriture de saisie ou de clôture la relit en bloc. */
export const CLE_COMPTABILITE = ['console', 'comptabilite'] as const;

/**
 * La lecture de la comptabilité. La clé porte le compte et son rôle, comme la
 * vue d'aigle : la réponse en cache d'un autre compte, ou d'un rôle d'avant,
 * n'est jamais servie.
 */
export function useLectureComptabilite() {
  const { moi } = useSession();
  const compteId = moi?.compte.id ?? null;
  const role = moi?.compte.rolePlateforme ?? null;
  return useQuery({
    queryKey: [...CLE_COMPTABILITE, compteId, role, 'lecture'],
    queryFn: lireComptabilite,
    enabled: moi !== undefined,
  });
}

/** Les versions de la grille et les bénéficiaires du partage : la direction qui règle la grille seule. */
export function useDonneesGrille(actif: boolean) {
  const { moi } = useSession();
  const compteId = moi?.compte.id ?? null;
  const versions = useQuery({
    queryKey: [...CLE_COMPTABILITE, compteId, 'versions'],
    queryFn: listerVersionsGrille,
    enabled: actif,
  });
  const beneficiaires = useQuery({
    queryKey: [...CLE_COMPTABILITE, compteId, 'beneficiaires'],
    queryFn: listerBeneficiaires,
    enabled: actif,
  });
  return { versions, beneficiaires };
}

/** Les types de recette, cumul et objectif compris : la direction seule. */
export function useTypesRecette(actif: boolean) {
  const { moi } = useSession();
  const compteId = moi?.compte.id ?? null;
  return useQuery({
    queryKey: [...CLE_COMPTABILITE, compteId, 'types'],
    queryFn: listerTypesRecette,
    enabled: actif,
  });
}

/** Les encaissements d'un mois et leur répartition : la direction seule. */
export function useEncaissements(mois: string, actif: boolean) {
  const { moi } = useSession();
  const compteId = moi?.compte.id ?? null;
  return useQuery({
    queryKey: [...CLE_COMPTABILITE, compteId, 'encaissements', mois],
    queryFn: () => listerEncaissements(mois),
    enabled: actif && mois !== '',
  });
}

/** Relit la comptabilité après une écriture (version, clôture, réouverture). */
export function useRelireComptabilite(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: CLE_COMPTABILITE }),
      client.invalidateQueries({ queryKey: ['console', 'vue-d-aigle'] }),
    ]);
  };
}
